import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./EasiScore.css";

const REGIONS = [
  { key: "easi.regions.headNeck", value: "Head & Neck" },
  { key: "easi.regions.hands", value: "Hands" },
  { key: "easi.regions.trunkGenitals", value: "Trunk & Genitals" },
  { key: "easi.regions.legs", value: "Legs" },
];

const INTENSITY_ROWS = [
  { key: "easi.intensity.redness", id: "rednessValue" },
  { key: "easi.intensity.thickness", id: "thicknessValue" },
  { key: "easi.intensity.scratching", id: "scratchingValue" },
  { key: "easi.intensity.lichenification", id: "lichenificationValue" },
];

const INTENSITY_COLS = [
  { key: "easi.intensity.none", val: "0" },
  { key: "easi.intensity.mild", val: "1" },
  { key: "easi.intensity.moderate", val: "2" },
  { key: "easi.intensity.severe", val: "3" },
];

const EasiScore = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";

  const [step, setStep] = useState(1);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [formData, setFormData] = useState([]);
  const [sliderValue, setSliderValue] = useState(0);
  const [radioValues, setRadioValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  const toggleRegion = (value) => {
    setSelectedRegions((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value]
    );
  };

  const buildQs = () =>
    `uuid=${uuid}&goqiiuserid=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&theme=${theme}&lang=${lang}`;

  const goToStep2 = () => {
    setCurrentIndex(0);
    setSliderValue(0);
    setRadioValues({});
    setFormData([]);
    setStep(2);
  };

  const currentRegion = selectedRegions[currentIndex];
  const isLastRegion = currentIndex === selectedRegions.length - 1;
  const intensityComplete = INTENSITY_ROWS.every((r) => radioValues[r.id] !== undefined);

  const advanceOrFinish = () => {
    const currentData = {
      [currentRegion]: { sliderValue, radioValue: radioValues },
    };

    if (isLastRegion) {
      if (submitting) return;
      setSubmitting(true);
      const allData = [...formData, currentData];
      lifestyleApi
        .logEasi({ goqiiUserId, formData: JSON.stringify(allData) })
        .then((res) => {
          if (String(res?.data?.code) === "200") {
            window.location.href = `/easidermadetails?${buildQs()}&type=easi`;
          }
        })
        .catch((err) => console.error("logEasi:", err))
        .finally(() => setSubmitting(false));
    } else {
      setFormData((prev) => [...prev, currentData]);
      setCurrentIndex((i) => i + 1);
      setSliderValue(0);
      setRadioValues({});
    }
  };

  if (step === 1) {
    return (
      <>
        <section className="easi-page">
          <div className="easi-container">
            <div className="easi-question-block">
              <label className="easi-label">{t("easi.selectRegionTitle")}</label>
              <div className="easi-region-list">
                {REGIONS.map(({ key, value }) => (
                  <label key={value} className="easi-region-item">
                    <input
                      type="checkbox"
                      checked={selectedRegions.includes(value)}
                      onChange={() => toggleRegion(value)}
                    />
                    <span
                      className={`easi-checkbox-box${selectedRegions.includes(value) ? " checked" : ""}`}
                    />
                    <span className="easi-region-label">{t(key)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </section>
        <section className="easi-bottom-btn">
          <button
            type="button"
            className="easi-btn-done"
            disabled={selectedRegions.length === 0}
            onClick={goToStep2}
          >
            {t("easi.nextBtn")}
          </button>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="easi-page">
        <div className="easi-container">
          <p className="easi-step-indicator">
            {currentIndex + 1} / {selectedRegions.length}
          </p>
          <p className="easi-region-title">{currentRegion}</p>

          {/* Area % slider */}
          <div className="easi-question-block">
            <label className="easi-label">{t("easi.areaQuestion")}</label>
            <p className="easi-slider-percent">{sliderValue}%</p>
            <input
              type="range"
              min={0}
              max={100}
              value={sliderValue}
              onChange={(e) => setSliderValue(Number(e.target.value))}
              className="easi-range-input"
              style={{
                background: `linear-gradient(to right, var(--color-primary, #35b400) ${sliderValue}%, #cccccc ${sliderValue}%)`,
              }}
            />
          </div>

          {/* Intensity table */}
          <div className="easi-question-block">
            <label className="easi-label">{t("easi.intensityQuestion")}</label>
            <div className="easi-table-wrap">
              <table className="easi-table">
                <thead>
                  <tr>
                    <th className="left-col" />
                    {INTENSITY_COLS.map((c) => (
                      <th key={c.val}>{t(c.key)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {INTENSITY_ROWS.map((row) => (
                    <tr key={row.id}>
                      <td className="left-col">{t(row.key)}</td>
                      {INTENSITY_COLS.map((c) => (
                        <td key={c.val}>
                          <label className="easi-radio-wrap">
                            <input
                              type="radio"
                              name={`easi-${currentIndex}-${row.id}`}
                              checked={radioValues[row.id] === c.val}
                              onChange={() =>
                                setRadioValues((prev) => ({ ...prev, [row.id]: c.val }))
                              }
                            />
                            <span
                              className={`easi-radio${radioValues[row.id] === c.val ? " checked" : ""}`}
                            />
                          </label>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="easi-bottom-btn">
        <button
          type="button"
          className="easi-btn-done"
          disabled={!intensityComplete || submitting}
          onClick={advanceOrFinish}
        >
          {isLastRegion ? t("easi.doneBtn") : t("easi.nextBtn")}
        </button>
      </section>
    </>
  );
};

export default EasiScore;
